import React, { useState, useEffect } from 'react';
import DashboardLayout from '../../components/DashboardLayout';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Plus, Edit, Trash2, Eye, EyeOff, Search, Copy } from 'lucide-react';
import api from '../../lib/api';
import { toast } from 'sonner';
import { DestructiveActionDialog } from '../../lib/DestructiveActionDialog';

const AdminPublications = () => {
  const [publications, setPublications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [editingPost, setEditingPost] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [publicationMode, setPublicationMode] = useState('draft');
  const [formData, setFormData] = useState({
    title: '',
    content: '',
    image: '',
    is_published: false,
    publish_at: ''
  });

  const toLocalDateTimeInput = (value) => {
    if (!value) return '';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '';
    return new Date(date.getTime() - date.getTimezoneOffset() * 60000)
      .toISOString().slice(0, 16);
  };

  useEffect(() => {
    loadPublications();
  }, []);

  const loadPublications = async () => {
    try {
      const response = await api.get('/publications?published_only=false');
      setPublications(response.data);
    } catch (error) {
      console.error('Error loading publications:', error);
      toast.error('Erreur lors du chargement des publications');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (post = null) => {
    if (post) {
      setEditingPost(post);
      const scheduledDate = post.publish_at ? new Date(post.publish_at) : null;
      setPublicationMode(
        !post.is_published ? 'draft' :
        scheduledDate && scheduledDate > new Date() ? 'scheduled' : 'immediate'
      );
      setFormData({
        title: post.title,
        content: post.content,
        image: post.image || '',
        is_published: post.is_published,
        publish_at: toLocalDateTimeInput(post.publish_at)
      });
    } else {
      setEditingPost(null);
      setPublicationMode('draft');
      setFormData({
        title: '',
        content: '',
        image: '',
        is_published: false,
        publish_at: ''
      });
    }
    setShowDialog(true);
  };

  const handleCloseDialog = () => {
    setShowDialog(false);
    setEditingPost(null);
    setPublicationMode('draft');
    setFormData({
      title: '',
      content: '',
      image: '',
      is_published: false,
      publish_at: ''
    });
  };

  const handleCopyLink = async () => {
    if (!editingPost?.slug) return;
    const articleUrl = `${window.location.origin}/blog/${editingPost.slug}`;
    try {
      await navigator.clipboard.writeText(articleUrl);
      toast.success('Lien de l’article copié');
    } catch (error) {
      toast.error('Impossible de copier le lien');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (publicationMode === 'scheduled') {
      if (!formData.publish_at) {
        toast.error('Choisis une date et une heure de publication');
        return;
      }
      if (new Date(formData.publish_at) <= new Date()) {
        toast.error('La date de publication doit être dans le futur');
        return;
      }
    }

    const payload = {
      ...formData,
      is_published: publicationMode !== 'draft',
      publish_at: publicationMode === 'scheduled'
        ? new Date(formData.publish_at).toISOString()
        : null
    };

    try {
      let response;
      if (editingPost) {
        response = await api.put(`/publications/${editingPost.id}`, payload);
        toast.success('Publication modifiée avec succès');
        loadPublications();
        handleCloseDialog();
      } else {
        response = await api.post('/publications', payload);
        toast.success('Publication créée avec succès. Voici son lien !');
        setEditingPost(response.data);
        loadPublications();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de l’enregistrement');
    }
  };

  const [deleteTarget, setDeleteTarget] = useState(null);

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await api.delete(`/publications/${deleteTarget.id}`);
      toast.success('Publication supprimée');
      setDeleteTarget(null);
      loadPublications();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const handleTogglePublish = async (post) => {
    try {
      await api.patch(`/publications/${post.id}/publish`, {
        is_published: !post.is_published
      });
      toast.success(post.is_published ? 'Publication dépubliée' : 'Publication publiée');
      loadPublications();
    } catch (error) {
      toast.error('Erreur lors de la modification du statut');
    }
  };

  const filteredPublications = publications.filter(pub =>
    pub.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
    pub.content.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-heading font-bold" data-testid="admin-publications-title">
              Publications
            </h1>
            <p className="text-muted-foreground mt-2">
              Gérez vos publications et contenus éditoriaux
            </p>
          </div>
          <Button onClick={() => handleOpenDialog()} data-testid="create-publication-button">
            <Plus className="h-4 w-4 mr-2" />
            Nouvelle Publication
          </Button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-white rounded-lg border p-6">
            <div className="text-sm text-muted-foreground mb-1">Total Publications</div>
            <div className="text-3xl font-bold">{publications.length}</div>
          </div>
          <div className="bg-white rounded-lg border p-6">
            <div className="text-sm text-muted-foreground mb-1">Publiées</div>
            <div className="text-3xl font-bold text-green-600">
              {publications.filter(p => p.is_published).length}
            </div>
          </div>
          <div className="bg-white rounded-lg border p-6">
            <div className="text-sm text-muted-foreground mb-1">Brouillons</div>
            <div className="text-3xl font-bold text-orange-600">
              {publications.filter(p => !p.is_published).length}
            </div>
          </div>
        </div>

        {/* Search */}
        <div className="flex gap-2">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Rechercher une publication..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
              data-testid="search-publications-input"
            />
          </div>
        </div>

        {/* Table */}
        <div className="bg-white rounded-lg border">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Titre</TableHead>
                  <TableHead>Statut</TableHead>
                  <TableHead>Date de création</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredPublications.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={4} className="text-center py-8 text-muted-foreground">
                      Aucune publication trouvée
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredPublications.map((pub) => (
                    <TableRow key={pub.id} data-testid={`publication-row-${pub.id}`}>
                      <TableCell>
                        <div className="font-medium">{pub.title}</div>
                        <div className="text-sm text-muted-foreground line-clamp-1">
                          {pub.content.substring(0, 100)}...
                        </div>
                      </TableCell>
                      <TableCell>
                        <span className={`px-2 py-1 text-xs rounded-md ${
                          pub.is_published 
                            ? 'bg-green-100 text-green-700' 
                            : 'bg-orange-100 text-orange-700'
                        }`}>
                          {pub.is_published ? 'Publiée' : 'Brouillon'}
                        </span>
                      </TableCell>
                      <TableCell>
                        {new Date(pub.created_at).toLocaleDateString('fr-FR')}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleTogglePublish(pub)}
                            data-testid={`toggle-publish-${pub.id}`}
                            title={pub.is_published ? 'Dépublier' : 'Publier'}
                          >
                            {pub.is_published ? (
                              <Eye className="h-4 w-4 text-green-600" />
                            ) : (
                              <EyeOff className="h-4 w-4 text-muted-foreground" />
                            )}
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleOpenDialog(pub)}
                            data-testid={`edit-publication-${pub.id}`}
                          >
                            <Edit className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setDeleteTarget(pub)}
                            data-testid={`delete-publication-${pub.id}`}
                          >
                            <Trash2 className="h-4 w-4 text-red-500" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          )}
        </div>

        {/* Dialog */}
        <Dialog open={showDialog} onOpenChange={handleCloseDialog}>
          <DialogContent className="flex max-h-[90vh] flex-col overflow-hidden sm:max-w-[700px]" data-testid="publication-modal">
            <DialogHeader>
              <DialogTitle>
                {editingPost ? 'Modifier la publication' : 'Nouvelle publication'}
              </DialogTitle>
            </DialogHeader>

            <form onSubmit={handleSubmit} className="flex min-h-0 flex-1 flex-col overflow-hidden">
              <div className="min-h-0 flex-1 space-y-4 overflow-y-auto px-1 py-1">
              <div className="space-y-2">
                <Label htmlFor="title">Titre *</Label>
                <Input
                  id="title"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  required
                  data-testid="publication-title-input"
                />
              </div>

              {editingPost?.slug && (
                <div className="space-y-2">
                  <Label htmlFor="article-link">Lien permanent de l’article</Label>
                  <div className="flex gap-2">
                    <Input
                      id="article-link"
                      value={`${window.location.origin}/blog/${editingPost.slug}`}
                      readOnly
                      className="bg-gray-50"
                    />
                    <Button type="button" variant="outline" onClick={handleCopyLink}>
                      <Copy className="h-4 w-4 mr-2" />
                      Copier
                    </Button>
                  </div>
                </div>
              )}

              <div className="space-y-2">
                <Label htmlFor="content">Contenu *</Label>
                <Textarea
                  id="content"
                  value={formData.content}
                  onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                  required
                  rows={10}
                  data-testid="publication-content-input"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="image">Image (URL)</Label>
                <Input
                  id="image"
                  value={formData.image}
                  onChange={(e) => setFormData({ ...formData, image: e.target.value })}
                  placeholder="https://..."
                  data-testid="publication-image-input"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="publication-mode">Statut de publication</Label>
                <select
                  id="publication-mode"
                  value={publicationMode}
                  onChange={(e) => setPublicationMode(e.target.value)}
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                  data-testid="publication-mode-select"
                >
                  <option value="draft">Brouillon</option>
                  <option value="immediate">Publier immédiatement</option>
                  <option value="scheduled">Programmer la publication</option>
                </select>
              </div>

              {publicationMode === 'scheduled' && (
                <div className="space-y-2">
                  <Label htmlFor="publish-at">Date et heure de publication</Label>
                  <Input
                    id="publish-at"
                    type="datetime-local"
                    value={formData.publish_at}
                    onChange={(e) => setFormData({ ...formData, publish_at: e.target.value })}
                    required
                    data-testid="publication-date-input"
                  />
                  <p className="text-xs text-muted-foreground">
                    L’article sera automatiquement publié à cette date et à cette heure.
                  </p>
                </div>
              )}

              </div>
              <div className="flex shrink-0 justify-end gap-2 border-t bg-white pt-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleCloseDialog}
                  data-testid="cancel-button"
                >
                  Annuler
                </Button>
                <Button type="submit" data-testid="save-publication-button">
                  {editingPost ? 'Mettre à jour' : 'Créer'}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>

        <DestructiveActionDialog
          open={!!deleteTarget}
          onOpenChange={() => setDeleteTarget(null)}
          title="Supprimer la publication"
          itemName={deleteTarget?.title || ''}
          description="La publication sera définitivement supprimée."
          onConfirm={handleDelete}
        />
      </div>
    </DashboardLayout>
  );
};

export default AdminPublications;
